import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, AlertTriangle, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { getCurrentFirebaseUser } from '@/lib/firebase';
import { bridge, BridgeChannels, BridgeActions } from '@/lib/bridge';
import { UserAPI } from '@shared/types';
import { useT } from '@/lib/i18n';
import { useAuth } from '@/lib/platform';

/**
 * On native, if the user signed in with Apple, revoke the Apple token
 * via Firebase's revokeTokenWithAuthorizationCode (handled on the native side).
 * Best-effort — failure does not block account deletion.
 */
async function revokeAppleTokenIfNeeded(): Promise<void> {
  if (!bridge.isNative || bridge.platform != 'ios') return;

  const user = getCurrentFirebaseUser();
  const isApple = user?.providerData.some(p => p.providerId === 'apple.com') ?? false;
  if (!isApple) return;

  try {
    await bridge.request(
      BridgeChannels.AUTH,
      BridgeActions.REVOKE_APPLE_TOKEN,
      {},
      60000 * 2
    );
  } catch (err) {
    console.warn('[DeleteAccount] Apple token revocation failed (non-blocking):', err);
  }
}

interface DeleteAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DeleteAccountModal({
  isOpen,
  onClose,
}: DeleteAccountModalProps) {
  const { t } = useT();
  const [confirmation, setConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { signOut } = useAuth();
  const isValid = confirmation.toLowerCase() === 'delete';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) {
      setError(t('deleteAccount.typeDeleteError'));
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      // Revoke Apple token on native before deleting (best-effort)
      await revokeAppleTokenIfNeeded();

      const response = await api.delete<UserAPI.DeleteAccountResponse>('/user/account');
      if (response.success) {
        await signOut();
        window.location.reload();
      } else {
        setError(response.data?.error || 'Failed to delete account');
      }
    } catch (err: any) {
      console.error('Error deleting account:', err);
      setError(err.response?.data?.error || t('deleteAccount.deleteFailed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setConfirmation('');
      setError('');
      onClose();
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
        >
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={handleClose}
          />
          
          {/* Dialog */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="relative bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl overflow-hidden max-w-md w-full"
          >
            {/* Close button */}
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="absolute top-4 right-4 z-10 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700 transition-colors disabled:opacity-50"
            >
              <X className="w-5 h-5 text-gray-600 dark:text-neutral-400" />
            </button>
            
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-neutral-700 bg-red-50 dark:bg-red-900/20">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-gray-900 dark:text-neutral-100">
                    {t('deleteAccount.title')}
                  </h2>
                  <p className="text-red-600 dark:text-red-400 text-sm">
                    {t('deleteAccount.cannotBeUndone')}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Content */}
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="text-gray-600 dark:text-neutral-300 space-y-3">
                <p>
                  {t('deleteAccount.confirmMessage')}
                </p>
                <ul className="list-disc list-inside text-sm space-y-1 text-gray-500 dark:text-neutral-400">
                  <li>{t('deleteAccount.removes.profile')}</li>
                  <li>{t('deleteAccount.removes.bookings')}</li>
                  <li>{t('deleteAccount.removes.chat')}</li>
                  <li>{t('deleteAccount.removes.subscriptions')}</li>
                </ul>
              </div>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
                  {error}
                </div>
              )}

              {/* Confirmation input */}
              <div>
                <label htmlFor="confirmation" className="block text-sm font-medium text-gray-700 dark:text-neutral-200 mb-1">
                  {t('deleteAccount.typeDelete')} <span className="font-bold text-red-600 dark:text-red-400">delete</span> {t('deleteAccount.toConfirm')}
                </label>
                <input
                  type="text"
                  id="confirmation"
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  placeholder={t('deleteAccount.placeholder')}
                  className="w-full px-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent bg-white dark:bg-neutral-700 text-gray-900 dark:text-neutral-100 placeholder-gray-400 dark:placeholder-neutral-500"
                  autoComplete="off"
                  disabled={isLoading}
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-neutral-600 rounded-lg hover:bg-gray-50 dark:hover:bg-neutral-700 transition-colors font-medium dark:text-neutral-200"
                  disabled={isLoading}
                >
                  {t('common.buttons.cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  disabled={!isValid || isLoading}
                >
                  <Trash2 className="w-4 h-4" />
                  {isLoading ? t('deleteAccount.deleting') : t('profile.account.deleteAccount')}
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
